import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Alert, 
  Space
} from 'antd';
import { 
  EyeInvisibleOutlined, 
  EyeOutlined, 
  KeyOutlined,
  MailOutlined,
  UserOutlined 
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text, Link } = Typography;

export default function Login() {
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, error: authError } = useAuth();
  const location = useLocation();

  // Synchroniser l'erreur du contexte avec l'état local
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // Rediriger vers la page précédente ou le tableau de bord après connexion
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      // Utiliser window.location pour éviter les problèmes de sécurité avec l'historique
      window.location.href = from;
    }
  }, [isAuthenticated, location.state]);

  // Configurer Axios pour intercepter les erreurs
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('Session expirée, veuillez vous reconnecter');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const handleSubmit = async (values: { email: string; password: string }) => {
    setError('');
    setLoading(true);
    
    try {
      await login(values.email ? values.email.trim() : '', values.password);
    } catch (err: any) {
      console.error('Login error:', err);
      // Récupérer le message d'erreur de différentes sources possibles
      const errorMessage = err?.message || err?.response?.data?.message || 'Échec de la connexion. Veuillez vérifier vos identifiants.';
      setError(errorMessage);
      setLoading(false);
      return; // S'arrêter ici pour éviter le finally qui remet loading à false
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Image */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("/maintenance_clim.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 0
        }}
      />
      
      {/* Gradient Overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(10, 84, 61, 0.85) 0%, rgba(13, 107, 77, 0.75) 50%, rgba(15, 125, 89, 0.85) 100%)',
          zIndex: 1
        }}
      />

      <div style={{ maxWidth: '520px', width: '100%', position: 'relative', zIndex: 2 }}>
        <Card
          style={{
            padding: '40px',
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
          }}
        >
          {/* Logo Container */}
          <div
            style={{
              width: '120px',
              height: '120px',
              background: 'linear-gradient(135deg, rgba(10, 84, 61, 0.1) 0%, rgba(15, 125, 89, 0.1) 100%)',
              borderRadius: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              border: '2px solid rgba(10, 84, 61, 0.2)',
              boxShadow: '0 10px 30px -10px rgba(10, 84, 61, 0.3)',
              padding: '16px'
            }}
          >
            <img 
              src="/logo-maintenance.png" 
              alt="Smart Maintenance Logo" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain' 
              }} 
            />
          </div>

          <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center', marginBottom: 32 }}>
            <Title level={2} style={{ margin: 0, color: '#0a543d', fontWeight: 700, letterSpacing: '0.5px' }}>
              Smart Maintenance
            </Title>
            <Text type="secondary" style={{ fontSize: '15px' }}>
              Connectez-vous à votre compte
            </Text>
          </Space>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              style={{ marginBottom: 24 }}
            />
          )}

          <Form
            form={form}
            name="login"
            onFinish={handleSubmit}
            layout="vertical"
            autoComplete="off"
            size="large"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Veuillez entrer votre email ou numéro de téléphone' }
              ]}
            >
              <Input
                prefix={
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      background: 'rgba(10, 84, 61, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 8
                    }}
                  >
                    <UserOutlined style={{ color: '#0a543d', fontSize: 20 }} />
                  </div>
                }
                placeholder="Email ou numéro de téléphone"
                style={{
                  borderRadius: '14px',
                  backgroundColor: '#f9fafb'
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Veuillez entrer votre mot de passe' }]}
            >
              <Input.Password
                prefix={
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      background: 'rgba(10, 84, 61, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 8
                    }}
                  >
                    <KeyOutlined style={{ color: '#0a543d', fontSize: 20 }} />
                  </div>
                }
                placeholder="Mot de passe"
                iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                style={{
                  borderRadius: '14px',
                  backgroundColor: '#f9fafb'
                }}
              />
            </Form.Item>

            <div style={{ textAlign: 'right', marginBottom: 16 }}>
              <Link
                href="/mot-de-passe-oublie"
                style={{
                  color: '#0a543d',
                  fontWeight: 500
                }}
              >
                Mot de passe oublié ?
              </Link>
            </div>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                style={{
                  height: '52px',
                  borderRadius: '14px',
                  fontSize: '16px',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #0a543d 0%, #0d6b4d 100%)',
                  border: 'none',
                  boxShadow: '0 8px 20px -8px rgba(10, 84, 61, 0.4)',
                  transition: 'all 0.3s ease'
                }}
              >
                Se connecter
              </Button>
            </Form.Item>
          </Form>

          {/* Footer */}
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              © 2026 Smart Maintenance by MCT• Version 1.0.0
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
}
